import React, { useState } from 'react'
import { FaCheck, FaTimes, FaExclamationTriangle, FaEye, FaImage } from 'react-icons/fa'
import { FaXmark } from 'react-icons/fa6'

const VPNMatchdayTable = ({ vpnTests, formatDate }) => {
  // State for screenshot dialog
  const [showScreenshotDialog, setShowScreenshotDialog] = useState(false)
  const [selectedScreenshot, setSelectedScreenshot] = useState(null)

  // Helper function to convert tinyint values to display strings
  const convertTinyintToDisplay = (value) => {
    if (value === 1) return 'Yes'
    if (value === 0) return 'No'
    return 'N/A'
  }

  // Get result icon and color
  const getResultIcon = (result) => {
    switch (result) {
      case 'Success':
        return <FaCheck className='text-green-500' />
      case 'Blocked':
        return <FaTimes className='text-red-500' />
      case 'Failed':
        return <FaExclamationTriangle className='text-yellow-500' />
      default:
        return <FaEye className='text-gray-500' />
    }
  }

  // Get result text color
  const getResultTextColor = (result) => {
    switch (result) {
      case 'Success':
        return 'text-green-600'
      case 'Blocked':
        return 'text-red-600'
      case 'Failed':
        return 'text-yellow-600'
      default:
        return 'text-gray-600'
    }
  }

  // Get result background color
  const getResultBackgroundColor = (result) => {
    switch (result) {
      case 'Success':
        return 'bg-green-100'
      case 'Blocked':
      case 'Failed':
      default:
        return 'bg-red-100'
    }
  }

  // Handle screenshot click
  const handleScreenshotClick = (screenshotPath) => {
    if (screenshotPath) {
      setSelectedScreenshot(`${process.env.REACT_APP_STATIC_URL}${screenshotPath}`)
      setShowScreenshotDialog(true)
    }
  }

  if (vpnTests.length === 0) {
    return (
      <div className='text-center py-12'>
        <FaEye className='w-12 h-12 text-gray-400 mx-auto mb-4' />
        <p className='text-gray-500 text-lg'>No VPN test data found for the selected filters.</p>
      </div>
    )
  }

  return (
    <>
      <div className='overflow-x-auto'>
        <table className='min-w-full bg-white border border-gray-200 rounded-lg'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Timestamp
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Result of the new test
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                In which country are you located?
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Which VPN did you use?
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Which broadcaster did you try?
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                For which country did you check?
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                VPN was activated, were you able to successfully register with the broadcaster using a French credit or
                debit card
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                While the VPN was activated, were you able to successfully watch Ligue 1 content or see advertisements
                for the Ligue?
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                While the VPN was deactivated, were you able to successfully watch Ligue 1 content or see advertisements
                for the Ligue?
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                VPN IP address
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Screenshot
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {vpnTests.map((test) => (
              <tr
                key={test.id}
                className='hover:bg-gray-50'
              >
                <td className='px-4 py-3 text-sm text-gray-900'>{formatDate(test.test_date)}</td>
                <td
                  className={`px-4 py-3 text-sm font-medium ${getResultBackgroundColor(
                    test.test_result
                  )} ${getResultTextColor(test.test_result)}`}
                >
                  {test.test_result}
                </td>
                <td className='px-4 py-3 text-sm text-gray-900'>{test.country_located || '-'}</td>
                <td className='px-4 py-3 text-sm text-gray-900'>{test.vpn_name}</td>
                <td className='px-4 py-3 text-sm text-gray-900'>{test.broadcaster_name}</td>
                <td className='px-4 py-3 text-sm text-gray-900'>{test.country_checked || '-'}</td>
                <td className='px-4 py-3 text-sm text-gray-900'>
                  {convertTinyintToDisplay(test.french_card_registration)}
                </td>
                <td className='px-4 py-3 text-sm text-gray-900'>
                  {convertTinyintToDisplay(test.ligue1_content_while_activated)}
                </td>
                <td className='px-4 py-3 text-sm text-gray-900'>
                  {convertTinyintToDisplay(test.ligue1_content_while_deactivated)}
                </td>
                <td className='px-4 py-3 text-sm text-gray-900'>{test.vpn_ip_address || '-'}</td>
                <td className='px-4 py-3 text-sm text-gray-900'>
                  {test.screenshot_path ? (
                    <button
                      onClick={() => handleScreenshotClick(test.screenshot_path)}
                      className='text-blue-600 hover:text-blue-800 cursor-pointer'
                    >
                      <FaImage className='w-4 h-4' />
                    </button>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Screenshot Dialog */}
      {showScreenshotDialog && (
        <div className='fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 animate-in fade-in duration-300'>
          <div className='bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300'>
            <div className='flex items-center justify-between mb-6'>
              <h3 className='text-xl font-bold text-gray-900'>Screenshot</h3>
              <button
                onClick={() => {
                  setShowScreenshotDialog(false)
                  setSelectedScreenshot(null)
                }}
                className='text-gray-400 hover:text-gray-600'
              >
                <FaXmark className='w-5 h-5' />
              </button>
            </div>

            <div className='flex justify-center'>
              <img
                src={selectedScreenshot}
                alt='Screenshot'
                className='max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg'
                onError={(e) => {
                  e.target.style.display = 'none'
                  e.target.nextSibling.style.display = 'block'
                }}
              />
              <div
                className='hidden max-w-full max-h-[70vh] flex items-center justify-center bg-gray-100 rounded-lg p-8'
                style={{ display: 'none' }}
              >
                <div className='text-center text-gray-500'>
                  <FaImage className='w-16 h-16 mx-auto mb-4 text-gray-300' />
                  <p className='text-lg font-medium'>Image not available</p>
                  <p className='text-sm'>The screenshot could not be loaded</p>
                </div>
              </div>
            </div>

            <div className='flex justify-end mt-6'>
              <button
                onClick={() => {
                  setShowScreenshotDialog(false)
                  setSelectedScreenshot(null)
                }}
                className='btn-secondary'
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default VPNMatchdayTable
